import { useTracking } from '@folklore/tracking';
import { getComponentFromName } from '@folklore/utils';
import classNames from 'classnames';
import { ComponentType, useEffect, useState } from 'react';

import * as CardComponents from '../cards/promotions';
import Modal from './Modal';

import styles from '../../styles/popups/promotion-modal.module.css';

type PromotionModalProps = {
    promotion: Promotion;
    delay?: number | null;
    className?: string | null;
    onClosed?: (() => void) | null;
} & Record<string, unknown>;

function PromotionModal({
    promotion,
    delay = null,
    className = null,
    onClosed = null,
    ...props
}: PromotionModalProps) {
    const [opened, setOpened] = useState(delay === null);

    useEffect(() => {
        if (delay === null) {
            return () => {};
        }
        const timeout = setTimeout(() => {
            setOpened(true);
        }, delay);
        return () => {
            clearTimeout(timeout);
        };
    }, [delay]);

    const onRequestClose = () => {
        setOpened(false);
    };

    const { id, handle, type } = promotion;
    const PromotionComponent = getComponentFromName(
        CardComponents,
        type,
        CardComponents.Default,
    ) as ComponentType<Record<string, unknown>> | null;

    const tracking = useTracking();
    const onPromotionComplete = () => {
        setOpened(false);
        tracking.trackPromotion(promotion, 'complete');
    };

    const onPromotionCancel = () => {
        setOpened(false);
        tracking.trackPromotion(promotion, 'cancel');
    };

    return PromotionComponent !== null ? (
        <Modal
            id={`promotion-${handle || id}`}
            opened={opened}
            withBackdrop
            withTransition
            className={classNames([styles.container, className])}
            onClosed={onClosed}
            requestClose={onRequestClose}
        >
            <PromotionComponent
                {...promotion}
                {...props}
                className={styles.card}
                onComplete={onPromotionComplete}
                onCancel={onPromotionCancel}
            />
        </Modal>
    ) : null;
}

export default PromotionModal;
